// BoardMenu — Chores' ONE chrome control, replacing the two circular buttons that ~15 sibling
// apps carry in the same corner at the same size. Identity pass 2026-08-30.
//
// The domain object is a WHITEBOARD, so the control is the thing that lives on one: a marker
// resting in its tray — a squared-off nib, not a circle, not a gear. The glyph matters more than
// the outline: a differently-clipped circle containing the same gear still reads as the same
// control to someone flipping screenshots.
//
// 🔴 It WRAPS the shared AccountMenu/BackupMenu completely unchanged. The account-deletion flow
// Guideline 5.1.1(v) depends on is reached through this control instead of beside it — the flow
// itself is untouched. Do not reimplement those menus here.
//
// ⚠️ The popover anchors LEFT (`right: 0` on a right-mounted control) and is width-capped at
// 88vw. A left-mounted popover anchored `right: 0` is how Reading shipped a Delete-account button
// at left:-142 — off-screen and unclickable — for eight days.

import { useState, type ReactNode } from "react";

export function BoardMenu({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <button
        className="och-tray"
        aria-label="Settings, backup and account"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {/* A marker lying in its tray — the object that belongs on a board. */}
        <svg width="19" height="19" viewBox="0 0 24 24" aria-hidden>
          <path d="M4 15.5 L14 5.5 L18.5 10 L8.5 20 L4 20 Z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="miter" />
          <path d="M14 5.5 L16 3.5 L20.5 8 L18.5 10 Z" fill="currentColor" />
          <path d="M3 22.2 H21" stroke="currentColor" strokeWidth="1.7" strokeLinecap="butt" />
        </svg>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div
            className="card och-tray-panel"
            style={{
              position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 50,
              width: "min(300px, 88vw)", padding: "0.85rem", textAlign: "left",
              // 🔴 NO maxHeight / overflowY here (2026-09-02). This panel holds two buttons; the
              // AccountMenu inside it opens its OWN absolutely-positioned popover, and a scroll box
              // here CLIPPED that popover to a sliver — the reviewer saw the avatar circle and
              // nothing else. That is the Apple 5.1.1(v) "no option to delete" rejection, twice.
              overflow: "visible",
            }}
          >
            {children}
          </div>
        </>
      )}
    </div>
  );
}
